import type { DishListing } from '../types/listing';

/**
 * Single source of truth for "can this listing still be ordered?".
 *
 * Three surfaces ask that question: the dish card, the activity centre's
 * stale-listing pill, and the listing detail page's order CTA. Each used to
 * derive it inline, and they drifted. The detail page's copy tested only
 * `sold_out`/`itemsRemaining`, so a canceled or expired listing kept a live
 * Order button while the card directly above it greyed out and read
 * "Unavailable". Retired listings are easy to land on (a share link outlives
 * its time window, and GET /listings/{id} serves any status to anyone), so
 * that CTA was reachable in ordinary use.
 *
 * Derive it here, not at the call site. A status added to `ListingStatus`
 * otherwise skips whichever surface forgets to handle it, silently.
 */

/**
 * Why a listing can't be ordered. Held apart from the display strings because
 * each surface names these in its own voice (`card.*` on the dish card,
 * `card.stale*` in the activity centre). Shared rule, local wording.
 */
export type UnavailableReason =
  /** Canceled, or expired when its time window closed. Retired, not just empty. */
  | 'retired'
  /** The lister stopped taking orders early (status `sold_out`). */
  | 'orders_closed'
  /** The portions ran out (status stays scheduled/active with nothing left). */
  | 'sold_out';

export interface ListingAvailability {
  /** Can't be ordered, for any reason. Gate order affordances on this. */
  unavailable: boolean;
  /** Why, when `unavailable`. Null when the listing is still orderable. */
  reason: UnavailableReason | null;
  /** Canceled or expired: the listing is retired rather than out of stock. */
  retired: boolean;
  /** Still listed, taking no more orders: stopped early, or nothing left. */
  soldOut: boolean;
}

/**
 * Every field is optional on purpose. `soldOut` is computed only by the feed
 * endpoints, summary payloads (order/activity) can omit the count, and the
 * dish card accepts a partial listing whose `status` may be absent. A payload
 * thin enough to be missing `status` can't be shown as unavailable, so it
 * reads as orderable and the server stays the gate.
 */
type AvailabilityInput = Partial<
  Pick<DishListing, 'status' | 'itemsRemaining' | 'soldOut' | 'dropoffRunStartedAt' | 'fulfillmentMethod'>
>;

const ORDERABLE: ListingAvailability = {
  unavailable: false,
  reason: null,
  retired: false,
  soldOut: false,
};

export function getListingAvailability(
  listing: AvailabilityInput | null | undefined,
): ListingAvailability {
  const status = listing?.status;
  // Drafts aren't orderable either, but they're the owner's own unpublished
  // listing: every surface labels them as a draft rather than as something
  // that got away, so they never carry an unavailable reason.
  if (!listing || !status || status === 'draft') return ORDERABLE;

  const retired = status === 'canceled' || status === 'expired';
  const soldOut =
    !retired &&
    (listing.soldOut === true ||
      status === 'sold_out' ||
      (listing.itemsRemaining != null && listing.itemsRemaining <= 0));

  if (retired) return { unavailable: true, reason: 'retired', retired: true, soldOut: false };
  if (soldOut) {
    return {
      unavailable: true,
      // `sold_out` means the lister closed orders by hand; so does a
      // drop-off-only listing whose run has started (the chef left, portions
      // may well remain). Anything else here means the count simply hit zero.
      reason:
        status === 'sold_out' || dropoffOnlyRunStarted(listing) ? 'orders_closed' : 'sold_out',
      retired: false,
      soldOut: true,
    };
  }
  return ORDERABLE;
}

/** A drop-off-only listing whose run has started. Eater payloads have already
 *  dropped `dropoff` from its methods by then, so "no pickup" is the test. */
function dropoffOnlyRunStarted(listing: AvailabilityInput): boolean {
  return !!listing.dropoffRunStartedAt && !listing.fulfillmentMethod?.includes('pickup');
}
